import React, { useCallback, useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';
import './App.css';
import SurfaceRenderer from './surface/SurfaceRenderer';

const API = process.env.REACT_APP_API_URL || '/api';

// Minimal, dependency-free, XSS-SAFE Markdown renderer. Echo's Layer-2 deep-review narrative is written
// by an LLM in Markdown; rendered as plain text the `##`/`**`/`-` show as literal characters. This turns
// the common subset (headings, bold, italic, inline code, bullet/numbered lists, paragraphs) into REACT
// ELEMENTS — never raw HTML / dangerouslySetInnerHTML — so any markup in the (semi-trusted) narrative is
// escaped by React and can never inject script. Deliberately small: not a full CommonMark parser.
function mdInline(text, keyPrefix) {
  // Tokenise **bold**, *italic*/_italic_, and `code` into React nodes; everything else is plain text.
  const nodes = [];
  const re = /(\*\*([^*]+)\*\*|`([^`]+)`|\*([^*]+)\*|_([^_]+)_)/g;
  let last = 0, m, i = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) nodes.push(text.slice(last, m.index));
    if (m[2] !== undefined) nodes.push(<strong key={`${keyPrefix}-b${i}`}>{m[2]}</strong>);
    else if (m[3] !== undefined) nodes.push(<code key={`${keyPrefix}-c${i}`}>{m[3]}</code>);
    else if (m[4] !== undefined) nodes.push(<em key={`${keyPrefix}-i${i}`}>{m[4]}</em>);
    else if (m[5] !== undefined) nodes.push(<em key={`${keyPrefix}-i${i}`}>{m[5]}</em>);
    last = m.index + m[0].length; i += 1;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}

function Markdown({ text }) {
  if (!text || typeof text !== 'string') return null;
  const lines = text.replace(/\r\n/g, '\n').split('\n');
  const blocks = [];
  let list = null; // { ordered, items: [] }
  let para = [];   // buffered paragraph lines
  const flushPara = () => { if (para.length) { blocks.push({ type: 'p', text: para.join(' ') }); para = []; } };
  const flushList = () => { if (list) { blocks.push({ type: 'list', ordered: list.ordered, items: list.items }); list = null; } };
  for (const raw of lines) {
    const line = raw.trimEnd();
    const h = line.match(/^(#{1,4})\s+(.*)$/);
    const ul = line.match(/^\s*[-*]\s+(.*)$/);
    const ol = line.match(/^\s*\d+[.)]\s+(.*)$/);
    if (h) { flushPara(); flushList(); blocks.push({ type: 'h', level: h[1].length, text: h[2] }); }
    else if (ul) { flushPara(); if (!list || list.ordered) { flushList(); list = { ordered: false, items: [] }; } list.items.push(ul[1]); }
    else if (ol) { flushPara(); if (!list || !list.ordered) { flushList(); list = { ordered: true, items: [] }; } list.items.push(ol[1]); }
    else if (line === '') { flushPara(); flushList(); }
    else { para.push(line); }
  }
  flushPara(); flushList();
  return (
    <div className="md">
      {blocks.map((b, i) => {
        if (b.type === 'h') { const H = `h${Math.min(b.level + 2, 6)}`; return React.createElement(H, { key: i }, mdInline(b.text, `h${i}`)); }
        if (b.type === 'list') {
          const L = b.ordered ? 'ol' : 'ul';
          return React.createElement(L, { key: i }, b.items.map((it, j) => <li key={j}>{mdInline(it, `l${i}-${j}`)}</li>));
        }
        return <p key={i}>{mdInline(b.text, `p${i}`)}</p>;
      })}
    </div>
  );
}
// Living Interface "Briefing" surface (D-237 / PRIN-026) — flag-gated; shadows the console home.
// Default ON; set REACT_APP_LI_BRIEFING=0 to hide the tab.
const LI_BRIEFING = (process.env.REACT_APP_LI_BRIEFING ?? '1') !== '0';
const TABS = [
  ...(LI_BRIEFING ? [{ id: 'briefing', label: 'Briefing' }] : []),
  { id: 'console', label: 'Console' },
  { id: 'court', label: 'Kingdom Court' },
  { id: 'knowledge', label: 'Knowledge Base' },
  { id: 'audit', label: 'Auditor' },
  { id: 'capabilities', label: 'Capabilities' },
  { id: 'tiers', label: 'Capabilities by Tier' },
];

// Live workload status → coloured pill (reuses the design-system status classes).
function StatusPill({ status }) {
  if (!status) return null;
  const cls = { reigning: 'crown', active: 'ok', dormant: 'warn', suspended: 'warn', degraded: 'err', declared: 'muted', external: 'muted', unknown: 'muted' }[status.state] || 'muted';
  return <span className={`status status-${cls}`} title={status.detail || ''}>{status.state}{status.detail ? ` · ${status.detail}` : ''}</span>;
}

// One node in the org tree; renders its card then its children below it (recursive).
// Clicking a card opens that agent's runtime detail panel (D-095).
// The five agent-lifecycle stages (L2 law). Each dot is an INDEPENDENT factual check, so a gap shows
// as an empty dot even when a LATER stage is filled — e.g. an agent that's deployed + running but
// never registered (③ empty), or registered but still pending_provision (⑤ empty). Filled = satisfied.
const LIFECYCLE_DOT_STAGES = ['Definition', 'Declaration', 'Nomination', 'Deployment', 'Active'];
function lifecycleDots(node) {
  const st = node.status && node.status.state;
  // No lifecycle for the human King or out-of-cluster advisors.
  if (node.kind === 'human' || st === 'reigning' || node.locus === 'external' || st === 'external') return null;
  const declaredOnly = (node.trigger || '').toLowerCase().includes('not yet staffed');
  const deployed = !declaredOnly && ['active', 'dormant', 'degraded', 'unknown'].includes(st);
  // Snapshot-backed (same source as the roster) — no separate live registry.list call.
  const registered = !!node.registered;                       // in the acquis roster: identity + entry
  const govActive = node.governance_status === 'active';      // formally activated (governance_status)
  return [
    true,                    // ① Definition — defined in the catalogue
    true,                    // ② Declaration — role declared in the acquis
    registered,              // ③ Nomination — identity + registry entry
    deployed,                // ④ Deployment — workload deployed in-cluster
    deployed && govActive,   // ⑤ Active — running AND governance status = active
  ];
}
// D-136 readiness: name the FIRST unmet stage in plain words so the King sees what an agent needs
// at a glance (e.g. "deployed, not activated" for a pending_provision officer).
const LIFECYCLE_UNMET_HINT = [
  'not defined',
  'not declared in the acquis',
  'not registered (no identity / registry entry)',
  'declared, not deployed',
  'deployed, not activated (pending_provision — seed not applied)',
];
function firstUnmetHint(dots) {
  if (!dots) return null;
  const i = dots.findIndex((d) => !d);
  return i < 0 ? null : LIFECYCLE_UNMET_HINT[i];
}
function LifecycleDots({ dots }) {
  if (!dots) return null;
  const done = dots.filter(Boolean).length;
  const detail = LIFECYCLE_DOT_STAGES.map((s, i) => `${dots[i] ? '●' : '○'} ${s}`).join('   ');
  return (
    <div className="court-dots" title={`Lifecycle ${done}/5\n${detail}`} aria-label={`Lifecycle ${done} of 5 stages complete`}>
      {dots.map((on, i) => <span key={i} className={`court-dot ${on ? 'on' : 'off'}`} />)}
    </div>
  );
}

// Prettify a kebab-case catalogue id for display (e.g. "prime-minister" → "prime minister").
const prettyId = (id) => (id || '').replace(/-/g, ' ');

function CourtNode({ node, childrenOf, registry, onSelect }) {
  const kids = childrenOf[node.id] || [];
  const dots = lifecycleDots(node);
  const needs = firstUnmetHint(dots);
  return (
    <li className="court-node">
      <div className={`court-card locus-${node.locus} clickable${node.council_seat ? ' council-seat-card' : ''}`} title="Click: agent detail" onClick={() => onSelect(node)}>
        <LifecycleDots dots={dots} />
        <div className="court-card-name">{node.name}</div>
        <div className="court-card-title">{node.title}</div>
        <div className="court-card-tags">
          {node.placement && <span className="tag tag-placement" title="Placement (seat / authority)">{prettyId(node.placement)}</span>}
          {node.tier && <span className={`tier-badge tier-${node.tier}`} title="Tier (derived from ring + exposure)">{node.tier}</span>}
          {node.locus === 'external' && <span className="tag tag-external">external</span>}
          {(node.capabilities || []).map((c) => <span key={c} className="tag" title="Capability (what it does)">{prettyId(c)}</span>)}
          {(node.council_seat || node.council_seat_status) && (
            <span className={`tag ${node.council_seat_status === 'reserved' ? 'tag-council-reserved' : 'tag-council'}`} title="Kingdom Council seat">
              {node.council_seat_status === 'reserved' ? 'Council · reserved' : `⚜ Council seat ${node.council_seat_index}`}
            </span>
          )}
          {node.autonomy && (
            <span
              className={`tag ${node.autonomy.active ? 'tag-autonomous' : 'tag-autonomous-pending'}`}
              title={`${node.autonomy.note || 'Autonomous authority'} (${node.autonomy.decree})`}
            >
              {`⟳ autonomous · ${node.autonomy.kind}${node.autonomy.active ? '' : ' (enabling)'}`}
            </span>
          )}
        </div>
        {node.model && <div className="court-card-model">{node.model}</div>}
        <StatusPill status={node.status} />
        {needs && <div className="court-card-needs" style={{ fontSize: '11px', color: 'var(--muted)', marginTop: '3px' }} title="First unmet lifecycle stage — what this agent needs next">needs: {needs}</div>}
      </div>
      {kids.length > 0 && (
        <ul className="court-children">
          {kids.map((k) => <CourtNode key={k.id} node={k} childrenOf={childrenOf} registry={registry} onSelect={onSelect} />)}
        </ul>
      )}
    </li>
  );
}

// One deep-dive block: a heading plus body / bullet list / numbered steps / ascii diagram.
function DeepBlock({ block }) {
  return (
    <div className="deep-block">
      <h4>{block.h}</h4>
      {block.body && <p>{block.body}</p>}
      {block.list && <ul className="deep-list">{block.list.map((x, i) => <li key={i}>{x}</li>)}</ul>}
      {block.steps && <ol className="deep-steps">{block.steps.map((x, i) => <li key={i}>{x}</li>)}</ol>}
      {block.diagram && <pre className="deep-diagram">{block.diagram}</pre>}
    </div>
  );
}

// Collapsible "Deep dive" — hidden by default so the panel stays small; expands + scrolls.
function DeepDive({ blocks }) {
  const [open, setOpen] = useState(false);
  if (!blocks || blocks.length === 0) return null;
  return (
    <div className="deep">
      <button className={`deep-toggle ${open ? 'open' : ''}`} onClick={() => setOpen((o) => !o)}>
        {open ? '▾ Hide deep dive' : `▸ Deep dive — how it really works (${blocks.length})`}
      </button>
      {open && <div className="deep-body">{blocks.map((b, i) => <DeepBlock key={i} block={b} />)}</div>}
    </div>
  );
}

// Detail panel for a contract ("how it works") or an instance (technical description).
function DetailModal({ detail, onClose }) {
  if (!detail) return null;
  const { kind, data } = detail;
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose} aria-label="Close">×</button>
        {kind === 'contract' ? (
          <>
            <h2>{data.name} <span className="modal-sub">— platform contract</span></h2>
            <p className="modal-lead">{data.detail?.what}</p>
            <h3>How it works</h3>
            <p>{data.detail?.how}</p>
            <h3>Underlying services</h3>
            <ul className="modal-links">
              {(data.detail?.infra || []).map((s) => (
                <li key={s.name}><a href={s.url} target="_blank" rel="noreferrer">{s.name} ↗</a></li>
              ))}
            </ul>
            <DeepDive blocks={data.detail?.more} />
          </>
        ) : (
          <>
            <h2>{data.name} <span className={`tier-badge tier-${data.tier}`}>{data.tier}</span></h2>
            <p className="modal-lead">{data.tech}</p>
            <h3>Contracts</h3>
            <p className="modal-oc">
              <strong>Offers:</strong> {data.offers?.length ? data.offers.join(', ') : '—'}<br />
              <strong>Consumes:</strong> {data.consumes?.length ? data.consumes.join(', ') : '—'}
            </p>
            <h3>Status</h3>
            <StatusPill status={data.status} />
            <DeepDive blocks={data.more} />
            <p className="modal-note">Functional role &amp; chain of command: see the Kingdom Court tab.</p>
          </>
        )}
      </div>
    </div>
  );
}

// Governance-lifecycle stepper: definition → declaration → nomination → deployment → active.
// The current stage is derived from facts server-side (live workload + declared signals).
function LifecycleStepper({ stages, current }) {
  const list = stages || [];
  const idx = list.indexOf(current);
  return (
    <div className="lifecycle">
      {list.map((s, i) => (
        <div key={s} className={`lc-step ${i < idx ? 'lc-done' : i === idx ? 'lc-current' : 'lc-pending'}`}>
          <span className="lc-dot" />
          <span className="lc-label">{s}</span>
        </div>
      ))}
    </div>
  );
}

// Agent runtime detail (D-095): lifecycle + identity + definition + the ACTUAL feed it loads +
// model. Read-only — the "Configuration" section is stubbed for future write-levers (model,
// temperature, …) gated on Q-036; token usage is a placeholder pending the LiteLLM source.
// Operational-config editor (agent-config). King-only: the browser calls the gateway with the King's
// OWN token, which the gateway resolves to ROLE-KING and uses to authorise agent.config.set. Operational
// params only (topics/model/temperature/max_tokens) — instructions are governed acquis (edited via
// Alfred). Saves are audited as ROLE-KING and take effect on the agent's next run.
const GATEWAY_MCP = 'https://agent-gateway.onmygarage.eu/mcp';
const fmtTok = (n) => (n >= 1000 ? `${(n / 1000).toFixed(n >= 100000 ? 0 : 1)}k` : String(n || 0));
const normId = (s) => (s || '').replace('@clients', '');

// One typed MCP call to the gateway with the King's own token. Extracted because three panels now
// make the identical call (config editor, janitor, knowledge base) — behaviour is unchanged.
async function gatewayCall(getToken, tool, input) {
  const token = await getToken();
  const r = await fetch(GATEWAY_MCP, {
    method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ tool, input: input || {} }),
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok || data.success === false) throw new Error(data.error || `HTTP ${r.status}`);
  return data.result;
}

function ConfigEditor({ agent, registryEntry }) {
  const { getAccessTokenSilently } = useAuth0();
  const fields = agent.config_fields || [];
  const showModel = fields.includes('model');
  const showTemp = fields.includes('temperature');
  const showTopics = fields.includes('topics');
  const editable = showModel || showTemp || showTopics;
  const roleId = registryEntry && registryEntry.role_id;
  const clientId = registryEntry && registryEntry.client_id;
  const defaultTopics = agent.default_topics || [];

  const [phase, setPhase] = useState('loading');   // loading | view | edit | saving
  const [err, setErr] = useState('');
  const [okMsg, setOkMsg] = useState('');
  const [cfg, setCfg] = useState({ topics: [], model: '', temperature: '' });   // saved override
  const [models, setModels] = useState([]);
  const [instr, setInstr] = useState(undefined);   // undefined=loading, null=unavailable, {instructions,mandates}
  const [usage, setUsage] = useState(undefined);   // undefined=loading, null=none, {month,all}
  const [fModel, setFModel] = useState('');
  const [fTemp, setFTemp] = useState('');
  const [fTopics, setFTopics] = useState('');

  const callGateway = (tool, input) => gatewayCall(getAccessTokenSilently, tool, input);

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const res = await callGateway('agent.config.get', { agent_id: agent.id });
        if (!live) return;
        const c = (res && res.config) || {};
        setCfg({ topics: c.topics || [], model: c.model || '', temperature: c.temperature ?? '' });
        setPhase('view');
      } catch (e) { if (live) { setErr(`Could not load config: ${e.message}`); setPhase('view'); } }
    })();
    // models (console API)
    (async () => {
      try {
        const token = await getAccessTokenSilently();
        const r = await axios.get(`${API}/kingdom/models`, { headers: { Authorization: `Bearer ${token}` } });
        if (live) setModels(r.data.models || []);
      } catch { /* dropdown falls back to current value only */ }
    })();
    // instructions (read-only, King-gated cross-role read)
    (async () => {
      if (!roleId) { if (live) setInstr(null); return; }
      try { const r = await callGateway('instruction.read_for_role', { role_id: roleId }); if (live) setInstr(r || null); }
      catch { if (live) setInstr(null); }
    })();
    // token usage (match this agent's client-id)
    (async () => {
      if (!clientId) { if (live) setUsage(null); return; }
      try {
        const r = await callGateway('usage.summary', {});
        const row = (r.by_identity || []).find((u) => normId(u.identity) === normId(clientId));
        if (live) setUsage(row ? { month: row.month, all: row.all } : { month: { input: 0, output: 0 }, all: { input: 0, output: 0 } });
      } catch { if (live) setUsage(null); }
    })();
    return () => { live = false; };
  }, [agent.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const startEdit = () => {
    setErr(''); setOkMsg('');
    setFModel(cfg.model || '');
    setFTemp(cfg.temperature === '' ? '' : String(cfg.temperature));
    setFTopics((cfg.topics.length ? cfg.topics : defaultTopics).join('\n'));
    setPhase('edit');
  };
  const save = async () => {
    setPhase('saving'); setErr(''); setOkMsg('');
    try {
      const input = { agent_id: agent.id };
      if (showTopics) input.topics = fTopics.split('\n').map((s) => s.trim()).filter(Boolean);
      if (showModel) input.model = fModel || undefined;
      if (showTemp) input.temperature = fTemp === '' ? undefined : Number(fTemp);
      const res = await callGateway('agent.config.set', input);
      setCfg({ topics: res.config.topics || (showTopics ? input.topics : cfg.topics), model: res.config.model || '', temperature: res.config.temperature ?? '' });
      setOkMsg('Saved ✓ — takes effect on the agent\'s next run.');
      setPhase('view');
    } catch (e) { setErr(`Save failed: ${e.message}`); setPhase('edit'); }
  };

  if (phase === 'loading') return <p className="modal-note">Loading configuration…</p>;

  const effTopics = cfg.topics.length ? cfg.topics : defaultTopics;
  const modelByValue = (v) => models.find((x) => x.value === v);
  const modelLabel = (v) => { const m = modelByValue(v); return m ? m.label : v; };
  // The model the cost is priced at: the one being picked (edit) or the saved/default one (view).
  const effModelValue = (phase === 'edit' ? fModel : cfg.model) || agent.default_model || '';
  const rateModel = modelByValue(effModelValue);
  const recModel = agent.recommended_model ? modelByValue(agent.recommended_model) : null;
  const fmtEur = (n) => (n == null ? '—' : n < 1 ? `€${n.toFixed(3)}` : `€${n.toFixed(2)}`);
  const rowCost = (row) => (rateModel && row ? (row.input * rateModel.in_eur + row.output * rateModel.out_eur) / 1e6 : null);
  // Prefer the AUTHORITATIVE per-call spend the gateway recorded from LiteLLM (usage.*.cost_eur);
  // fall back to the model-rate estimate only for older calls that predate cost recording.
  const authCost = (row) => (row && typeof row.cost_eur === 'number' && row.cost_eur > 0 ? row.cost_eur : rowCost(row));

  return (
    <div className="config-editor">
      {showModel && agent.recommended_model && (
        <div className="cfg-rec">
          <div className="cfg-rec-head">
            <span className="cfg-rec-star">★</span> Recommended:{' '}
            <strong>{recModel ? recModel.label : agent.recommended_model}</strong>
            {phase === 'edit' && fModel !== agent.recommended_model && (
              <button type="button" className="cfg-rec-apply" onClick={() => setFModel(agent.recommended_model)}>use this</button>
            )}
          </div>
          {agent.recommended_reason && <div className="cfg-rec-why">{agent.recommended_reason}</div>}
        </div>
      )}
      {phase === 'edit' ? (
        <div className="cfg-form">
          {showModel && (
            <label className="cfg-label">Model
              <select className="cfg-input" value={fModel} onChange={(e) => setFModel(e.target.value)}>
                <option value="">(default — {agent.model})</option>
                {models.map((m) => <option key={m.value} value={m.value}>{m.label}{m.note ? ` — ${m.note}` : ''}</option>)}
              </select>
            </label>
          )}
          {showTemp && (
            <label className="cfg-label">Temperature <span className="modal-sub">(0–1, blank = provider default)</span>
              <input className="cfg-input" type="number" min="0" max="1" step="0.1" value={fTemp} onChange={(e) => setFTemp(e.target.value)} />
            </label>
          )}
          {showTopics && (
            <label className="cfg-label">Watch-topics <span className="modal-sub">(one per line)</span>
              <textarea className="cfg-input" rows={8} value={fTopics} onChange={(e) => setFTopics(e.target.value)} />
            </label>
          )}
          <div className="cfg-actions">
            <button className="cfg-save" disabled={phase === 'saving'} onClick={save}>{phase === 'saving' ? 'Saving…' : 'Save'}</button>
            <button className="cfg-cancel" disabled={phase === 'saving'} onClick={() => { setPhase('view'); setErr(''); }}>Cancel</button>
          </div>
          {err && <p className="cfg-err">{err}</p>}
        </div>
      ) : (
        <>
          <div className="cfg-view">
            {showModel && <div className="cfg-view-row"><span className="cfg-k">Model</span><span className="cfg-v">{cfg.model ? modelLabel(cfg.model) : <em>default — {agent.model}</em>}</span></div>}
            {showTemp && <div className="cfg-view-row"><span className="cfg-k">Temperature</span><span className="cfg-v">{cfg.temperature !== '' ? cfg.temperature : <em>provider default</em>}</span></div>}
            {showTopics && (
              <div className="cfg-view-row"><span className="cfg-k">Watch-topics {cfg.topics.length ? '' : <span className="modal-sub">(built-in)</span>}</span>
                <span className="cfg-v">{effTopics.length ? <ul className="cfg-topics">{effTopics.map((t, i) => <li key={i}>{t}</li>)}</ul> : <em>none</em>}</span>
              </div>
            )}
            {!editable && <p className="modal-note">No tunable model parameters — this agent is deterministic (no LLM).</p>}
          </div>
          {editable && <button className="cfg-edit" onClick={startEdit}>Edit configuration</button>}
          {okMsg && <p className="cfg-ok">{okMsg}</p>}
          {err && <p className="cfg-err">{err}</p>}
        </>
      )}

      <h3>Token usage &amp; spend</h3>
      {usage === undefined ? <p className="modal-note">Loading…</p>
        : usage === null ? <p className="modal-note">No usage data{clientId ? '' : ' (agent not registered)'}.</p>
        : (() => {
            const sameMonthAsAll = usage.month.input === usage.all.input && usage.month.output === usage.all.output;
            const recCost = (row) => (recModel && row ? (row.input * recModel.in_eur + row.output * recModel.out_eur) / 1e6 : null);
            // Real spend recorded by the gateway from LiteLLM (per call), not a re-estimate.
            const costIsReal = (usage.month.cost_eur > 0) || (usage.all.cost_eur > 0);
            const mCost = authCost(usage.month);
            const mCalls = usage.month.calls || 0;
            const perCall = mCost != null && mCalls ? mCost / mCalls : null;
            return (
              <>
                {rateModel && (
                  <p className="cfg-rate">
                    Estimated at <strong>{rateModel.label}</strong>{phase === 'edit' ? ' (selected)' : (cfg.model ? '' : ' (default)')}:{' '}
                    {fmtEur(rateModel.in_eur)} / {fmtEur(rateModel.out_eur)} per 1M tokens (in / out)
                    {rateModel.provider === 'anthropic' && <span className="modal-sub"> · USD list ≈EUR</span>}
                  </p>
                )}
                <table className="cfg-usage">
                  <thead><tr><th></th><th>Input</th><th>Output</th><th>Calls</th><th>{costIsReal ? 'Spend (LiteLLM)' : `Cost @ ${rateModel ? rateModel.label : 'model'}`}</th></tr></thead>
                  <tbody>
                    <tr><td>This month</td><td>{fmtTok(usage.month.input)}</td><td>{fmtTok(usage.month.output)}</td><td>{usage.month.calls || 0}</td><td>{fmtEur(mCost)}</td></tr>
                    {!sameMonthAsAll && <tr><td>All time</td><td>{fmtTok(usage.all.input)}</td><td>{fmtTok(usage.all.output)}</td><td>{usage.all.calls || 0}</td><td>{fmtEur(authCost(usage.all))}</td></tr>}
                  </tbody>
                </table>
                {rateModel ? (
                  <p className="modal-sub">
                    {perCall != null && <>≈ <strong>{fmtEur(perCall)}</strong> per call over {mCalls} call{mCalls === 1 ? '' : 's'} this month. </>}
                    {sameMonthAsAll && <>All usage to date is from this month. </>}
                    {costIsReal
                      ? <><strong>Actual spend</strong> recorded by the gateway from LiteLLM at each call (authoritative, whatever model ran it).</>
                      : <>This is the measured volume <em>priced at {rateModel.label}</em> for comparison — not actual spend if another model ran the calls.</>}
                    {phase !== 'edit' && recModel && cfg.model !== agent.recommended_model && recCost(usage.month) != null &&
                      <> On the recommended {recModel.label} the same volume would price at ≈ <strong>{fmtEur(recCost(usage.month))}</strong> ({fmtEur(mCalls ? recCost(usage.month) / mCalls : null)}/call).</>}
                  </p>
                ) : <p className="modal-sub">{costIsReal ? 'Cost column is actual spend recorded from LiteLLM per call.' : 'Pick a model (Edit configuration) to price this usage.'}</p>}
              </>
            );
          })()}

      <h3>Instructions <span className="modal-sub">(read-only — quote the id to Alfred to change one)</span></h3>
      {instr === undefined ? <p className="modal-note">Loading…</p>
        : instr === null ? <p className="modal-note">Instructions unavailable for this agent.</p>
        : (instr.instructions && instr.instructions.length)
          ? <ul className="cfg-instructions">{instr.instructions.map((i) => <li key={i.id}><code className="instr-id">{i.id}</code>{i.name ? <strong> {i.name}</strong> : ''}{i.text ? `: ${i.text}` : ''}</li>)}</ul>
          : <p className="modal-note">No role-specific instructions recorded (governed by the shared L1/L2 acquis).</p>}

      <p className="modal-note">Model / temperature / topics are operational — saved as you (audited as ROLE-KING), applied on the next run. Instructions are governed acquis, edited via Alfred.</p>
    </div>
  );
}

function AgentDetailModal({ agent, packet, registryEntry, placements, onClose }) {
  const [showDef, setShowDef] = useState(false);
  const [showFeed, setShowFeed] = useState(false);
  if (!agent) return null;
  const dots = lifecycleDots(agent);
  const p = packet?.available ? packet.packet : null;
  const placement = (placements || []).find((x) => x.id === agent.placement);
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose} aria-label="Close">×</button>
        <h2>{agent.name} <span className="modal-sub">— {agent.title}</span></h2>

        <h3>Lifecycle <span className="modal-sub">(factually checked: deployment/active are live)</span></h3>
        <LifecycleStepper stages={agent.lifecycle_stages} current={agent.lifecycle} />
        {dots && (
          <div className="lifecycle-breakdown">
            <ul className="lc-stage-list">
              {LIFECYCLE_DOT_STAGES.map((s, i) => (
                <li key={s} className={dots[i] ? 'lc-on' : 'lc-off'}><span className="lc-mark">{dots[i] ? '●' : '○'}</span> {s}</li>
              ))}
            </ul>
            {registryEntry
              ? <p className="modal-note">Registry: <strong>{registryEntry.status}</strong>{registryEntry.status === 'suspended'
                  ? ' — SUSPENDED: the office exists and is not retired, but its runtime is stopped. It is between mandates.'
                  : registryEntry.status !== 'active' && ' — formal activation (a set_status decree) is still pending, so the Active dot is open.'}</p>
              : <p className="modal-note">Not in the agent registry yet — the Nomination dot is open until it's registered (a King decree).</p>}
          </div>
        )}

        {/* One-line summary, with the full declaration definition behind a toggle. */}
        <p className="modal-lead">{agent.description}</p>
        <button className={`deep-toggle ${showDef ? 'open' : ''}`} onClick={() => setShowDef((v) => !v)}>
          {showDef ? '▾ Hide definition' : '▸ Full declaration / definition'}
        </button>
        {showDef && (
          <div className="deep-body">
            <div className="deep-block">
              <p>Carries the <strong>full L1 constitution + L2 behavioural mandates</strong> (shared by every agent), assembled at startup via the gateway. Below: this agent's specifics.</p>
              <p className="modal-oc">
                <strong>Capabilities:</strong> {(agent.capabilities || []).join(', ') || '—'}<br />
                {p?.role?.scopes && <><strong>Scopes:</strong> {p.role.scopes.join(', ')}<br /></>}
                <strong>Role:</strong> {agent.description}
              </p>
            </div>
          </div>
        )}

        <h3>Identity &amp; role</h3>
        <p className="modal-oc modal-axes">
          <strong>Capability:</strong> {(agent.capabilities || []).map(prettyId).join(', ') || '—'} <span className="axis-note">(what it does)</span><br />
          <strong>Placement:</strong> {placement ? placement.name : (agent.placement ? prettyId(agent.placement) : '—')} <span className="axis-note">(where it sits / its authority)</span><br />
          <strong>Tier:</strong> {agent.tier ? <span className={`tier-badge tier-${agent.tier}`}>{agent.tier}</span> : '—'} <span className="axis-note">(derived from ring + exposure, D-120)</span>
        </p>
        {placement && <p className="modal-note">{placement.summary}</p>}
        <p className="modal-oc">
          <strong>Id:</strong> {agent.id}<br />
          <strong>Ministry:</strong> {agent.ministry}<br />
          <strong>Reports to:</strong> {agent.reports_to || '— (sovereign)'}<br />
          <strong>Trigger:</strong> {agent.trigger || '—'}
        </p>
        <h3>Status</h3>
        <StatusPill status={agent.status} />

        <h3>Configuration</h3>
        {agent.configurable
          ? <ConfigEditor agent={agent} registryEntry={registryEntry} />
          : (
            <>
              <p className="modal-oc"><strong>Model:</strong> {agent.model || (p?.model ? `${p.model.name} · ${p.model.via}` : '—')}</p>
              <p className="modal-note">Model, temperature and watch-topics are editable here for in-cluster agents that read a live profile; this entry doesn't have one.</p>
            </>
          )}

        {agent.has_packet && (
          <>
            <h3>Loaded feed <span className="modal-sub">(what it actually receives at startup)</span></h3>
            {!packet && <p className="modal-note">Loading…</p>}
            {packet && !packet.available && <p className="modal-note">Feed unavailable: {packet.reason}</p>}
            {p && (
              <>
                <p className="modal-oc">
                  <strong>Assembled via:</strong> {p.assembled_via}<br />
                  <strong>Tool surface:</strong> {(p.tool_surface || []).join(', ')}<br />
                  <strong>Feed size:</strong> {p.system_prompt_chars} chars
                </p>
                <ul className="agent-sections">
                  {(p.sections || []).map((s) => {
                    const bits = [];
                    if (typeof s.count === 'number') bits.push(`${s.count} item(s)`);
                    if (s.present !== undefined) bits.push(`present: ${s.present ? 'yes' : 'no'}`);
                    if (s.fields) bits.push(`fields: ${s.fields.join(', ')}`);
                    if (s.anti_patterns_present !== undefined) bits.push(`anti_patterns: ${s.anti_patterns_present ? '✓ present' : '✗ MISSING'}`);
                    return (
                      <li key={s.name}>
                        <strong>{s.name}</strong> <span className="modal-sub">{s.source}</span>
                        <div className="agent-section-detail">{bits.join(' · ')}</div>
                        {s.items && s.items.length > 0 && (
                          <div className="agent-section-items">{s.items.slice(0, 16).join(', ')}{s.items.length > 16 ? '…' : ''}</div>
                        )}
                      </li>
                    );
                  })}
                </ul>
                {p.system_prompt && (
                  <>
                    <button className={`deep-toggle ${showFeed ? 'open' : ''}`} onClick={() => setShowFeed((v) => !v)}>
                      {showFeed ? '▾ Hide the actual feed' : '▸ Show the actual feed (verbatim)'}
                    </button>
                    {showFeed && <pre className="agent-feed">{p.system_prompt}</pre>}
                  </>
                )}
              </>
            )}
          </>
        )}
        <p className="modal-note">Runtime view. Functional role &amp; chain of command are shown on the Court tree itself.</p>
      </div>
    </div>
  );
}

// Finesse → meaning, shown in the Janitor panel so the King knows what the dial does.
const FINESSE_GUIDE = [
  { v: '0.95–1.00', label: 'Very fine / strict', meaning: 'Only near-identical notes count as the same — almost no false merges. Catches the least, safest.' },
  { v: '0.85–0.90', label: 'Fine (default)', meaning: 'Same finding, lightly reworded. The recommended working range for the officer\'s digests.' },
  { v: '0.70–0.85', label: 'Moderate', meaning: 'Same topic, different wording. Starts catching "related but not identical" — review before acting.' },
  { v: '0.50–0.70', label: 'Coarse / loose', meaning: 'Same theme only. Many pairs; high risk of merging genuinely distinct notes. Measurement, not cleanup.' },
];

function JanitorPanel() {
  const { getAccessTokenSilently } = useAuth0();
  const [health, setHealth] = useState(undefined);   // undefined=loading, null=error, {...}
  const [finesse, setFinesse] = useState(0.9);
  const [clusters, setClusters] = useState(undefined); // undefined=not run, null=error, {...}
  const [scanning, setScanning] = useState(false);
  const [err, setErr] = useState('');
  const [savedFinesse, setSavedFinesse] = useState(null); // the janitor's persisted operating finesse
  const [floor, setFloor] = useState(0.8);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');

  const callGateway = (tool, input) => gatewayCall(getAccessTokenSilently, tool, input);

  useEffect(() => {
    let live = true;
    (async () => {
      try { const h = await callGateway('knowledge.health', {}); if (live) setHealth(h); }
      catch (e) { if (live) { setHealth(null); setErr(`Could not load health: ${e.message}`); } }
      // Load the janitor's PERSISTED operating finesse so the bar opens where the King left it.
      try {
        const f = await callGateway('janitor.get_finesse', {});
        if (live && f && Number.isFinite(Number(f.merge_min_sim))) {
          setSavedFinesse(Number(f.merge_min_sim));
          setFinesse(Number(f.merge_min_sim));
          if (Number.isFinite(Number(f.floor))) setFloor(Number(f.floor));
        }
      } catch { /* get_finesse optional — bar just starts at its default */ }
    })();
    return () => { live = false; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const runScan = async () => {
    setScanning(true); setErr('');
    try { const c = await callGateway('knowledge.neardup_clusters', { min_similarity: finesse, limit: 50 }); setClusters(c); }
    catch (e) { setClusters(null); setErr(`Scan failed: ${e.message}`); }
    finally { setScanning(false); }
  };

  // Persist the finesse as the janitor's operating merge threshold (King authority; the console calls
  // as ROLE-KING). The backend clamps to the safety floor and returns the effective value.
  const saveFinesse = async () => {
    setSaving(true); setSaveMsg(''); setErr('');
    try {
      const r = await callGateway('janitor.set_finesse', { merge_min_sim: finesse });
      const eff = Number(r?.merge_min_sim);
      setSavedFinesse(eff);
      setSaveMsg(r?.floored
        ? `Saved. Requested ${finesse.toFixed(2)}; the janitor will operate at ${eff.toFixed(2)} (minimum ${floor.toFixed(2)} — looser merges are too aggressive to run unattended).`
        : `Saved. The janitor will merge near-duplicates at finesse ${eff.toFixed(2)} from its next run.`);
    } catch (e) { setSaveMsg(''); setErr(`Save failed: ${e.message}`); }
    finally { setSaving(false); }
  };

  const nd = health && health.near_duplicates;
  return (
    <section className="section">
      <h2>The Janitor <span className="modal-sub">— knowledge-base hygiene (Knowledge Ministry)</span></h2>
      <p className="section-intro">
        The janitor keeps the knowledge base lean: it finds <strong>near-duplicate</strong> notes (the same finding
        re-summarised across cycles) so they can be consolidated. The <strong>acquis (the constitution & laws) is fenced</strong> —
        the janitor never touches it. Today it runs <strong>flag-only</strong> (it shows you what it would target, writes nothing);
        new redundancy is already prevented at the source (the officer reinforces a recurring finding's <code>days_seen</code> instead of writing a copy).
      </p>

      <h3>Knowledge base now</h3>
      {health === undefined ? <p className="modal-note">Loading…</p>
        : health === null ? <p className="cfg-err">{err}</p>
        : (
          <p className="modal-oc">
            <strong>Total notes:</strong> {health.notes?.total ?? '—'}<br />
            <strong>Exact duplicates:</strong> {health.notes?.redundant_duplicate_copies ?? 0} · <strong>empty/tiny:</strong> {health.notes?.empty_or_tiny ?? 0}<br />
            <strong>Near-duplicate pairs:</strong> {nd && nd.available
              ? <>≥0.85: {nd.pairs_ge_0_85} · ≥0.70: {nd.pairs_ge_0_70} · ≥0.50: {nd.pairs_ge_0_50}</>
              : <span className="modal-sub">{nd?.note || 'unavailable'}</span>}
          </p>
        )}

      <h3>Finesse <span className="modal-sub">— how alike two notes must be before the janitor treats them as the same</span></h3>
      <div className="cfg-form">
        <label className="cfg-label">Finesse: <strong>{finesse.toFixed(2)}</strong>
          <input type="range" min="0.5" max="1" step="0.05" value={finesse}
                 onChange={(e) => setFinesse(Number(e.target.value))} className="cfg-input" />
        </label>
        <p className="modal-sub">Higher = finer/stricter (fewer, near-identical only). Lower = coarser/looser (more, same-topic).</p>
        <table className="data-table">
          <thead><tr><th>Value</th><th>Meaning</th></tr></thead>
          <tbody>
            {FINESSE_GUIDE.map((g) => (
              <tr key={g.v}><td><code>{g.v}</code> — {g.label}</td><td>{g.meaning}</td></tr>
            ))}
          </tbody>
        </table>
        <p className="modal-sub">
          Janitor's saved operating finesse: <strong>{savedFinesse == null ? '—' : savedFinesse.toFixed(2)}</strong>
          {savedFinesse != null && Math.abs(savedFinesse - finesse) > 0.001 && <> · unsaved change to <strong>{finesse.toFixed(2)}</strong></>}
        </p>
        <div className="cfg-actions">
          <button className="cfg-save" disabled={scanning} onClick={runScan}>{scanning ? 'Scanning…' : `Preview at finesse ${finesse.toFixed(2)}`}</button>
          <button className="cfg-save" disabled={saving} onClick={saveFinesse}>{saving ? 'Saving…' : 'Save as janitor setting'}</button>
        </div>
        {saveMsg && <p className="modal-note">{saveMsg}</p>}
        {err && clusters === null && <p className="cfg-err">{err}</p>}
      </div>

      {clusters && clusters.available && (
        <>
          <h3>What the janitor would flag at finesse {clusters.min_similarity} <span className="modal-sub">(read-only — nothing changed)</span></h3>
          <p className="modal-oc"><strong>{clusters.total_pairs}</strong> near-duplicate pair(s); showing {clusters.shown}. Same title across different dates → supersede (keep newest), not merge.</p>
          <div className="matrix-scroll">
            <table className="data-table">
              <thead><tr><th>Sim</th><th>Note A (date)</th><th>Note B (date)</th></tr></thead>
              <tbody>
                {(clusters.pairs || []).map((p, i) => (
                  <tr key={i}>
                    <td><strong>{p.sim}</strong></td>
                    <td>{p.a_title} <span className="modal-sub">{(p.a_date || '').slice(0, 10)}</span></td>
                    <td>{p.b_title} <span className="modal-sub">{(p.b_date || '').slice(0, 10)}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
      {clusters && !clusters.available && <p className="modal-note">{clusters.note}</p>}
    </section>
  );
}

// ── Knowledge Base (King 2026-08-17) ────────────────────────────────────────────────────────────
// The whole knowledge base in one page: what it is, what is in it, how fresh, who may read or write.
//
// It composes two sources that need NO knowledge:read scope — which is the only reason the King's
// console token can render it at all:
//   • /api/kingdom/knowledge  — the stable schematic: registries, access model, explanations.
//   • gateway knowledge.health — the live numbers (counts, oldest/newest, by-author, noise).
// Anything returning note CONTENT (note.search, principle.get_all, graph.export) is gated on
// knowledge:read, which this token does not hold — that is Phase 2, not this page.
//
// Fetches on mount rather than in App.load(), so opening the console never pays for a tab the King
// did not open. It renders its own knowledge.health call; the Janitor panel below makes the same
// call independently, which keeps that panel self-contained and still reachable from its agent card.

const dateOnly = (s) => (s ? String(s).slice(0, 10) : '—');

// A store's live count. `count_from` is either "notes_total" or "graph_label:<Label>"; null means
// this registry has no single meaningful count (e.g. conversations) and shows an em dash.
function storeCount(store, health) {
  const from = store.count_from;
  if (!from || !health) return null;
  if (from === 'notes_total') return health.notes?.total ?? null;
  if (from === 'derivations_total') return health.derivations?.total ?? null;
  if (from === 'conclusions_total') return health.derivations?.conclusions_total ?? null;
  if (from.startsWith('graph_label:')) {
    const want = from.slice('graph_label:'.length);
    const row = (health.graph_node_labels || []).find((r) => (r.labels || []).includes(want));
    return row ? row.count : 0;   // absent label = genuinely zero, not unknown
  }
  return null;
}

// ── B-127: the derivation graph view (D-219(5), a committed deliverable) ───────────────────────────
// The King wants to SEE structure, not read rows. This renders knowledge.derivation_trace as a picture:
// the derived claim, the sources it was built from (wasDerivedFrom, PROV-O), and — across a red
// `contradicts` edge — the opposing claim and ITS sources. Both ends land on real notes. Every node is
// clickable to re-root the trace there, so the King can walk the provenance. The trace tool is ungated at
// the gateway (like knowledge.health), which is the only reason the King's SPA token can render this at all.
// Starting points come from health.derivations.recent (the trace is per-id and can't discover claims itself).

const truncate = (s, n) => { const t = String(s || ''); return t.length > n ? t.slice(0, n - 1) + '…' : t; };

// D-230 1.2: a derived claim is `confirmed` (machine-tier — the Minister approved it, NO royal endorsement)
// unless the King has explicitly endorsed it into a `conclusion`. Every surface must show which; presenting a
// confirmed claim as King-endorsed is a defect (D-176).
function ClaimStatus({ status }) {
  if (status === 'conclusion') return <span style={{ color: 'var(--blue)', fontWeight: 600 }} title="The King has explicitly endorsed this claim">♛ King-endorsed</span>;
  if (status === 'confirmed') return <span className="modal-sub" title="Machine-confirmed — approved into the knowledge base, but the King has not endorsed it">machine-confirmed</span>;
  return <span className="modal-sub">—</span>;
}

// Lay the flat trace rows out as a two-band diagram: the derivation on the left, the contradicting claim
// and its sources on the right. Pure geometry, no dependency. Tier = wasDerivedFrom distance from each
// band's root; nodes in a tier spread horizontally. from_id gives every real edge.
const DG = { NODE_W: 168, NODE_H: 48, H_GAP: 22, V_GAP: 60, MARGIN: 18, BAND_GAP: 90 };
function computeDerivationLayout(trace) {
  if (!Array.isArray(trace) || !trace.length) return null;
  const nodes = new Map(); const edges = [];
  for (const r of trace) {
    if (!nodes.has(r.node_id)) nodes.set(r.node_id, { id: r.node_id, role: r.role, title: r.title || r.node_id, reconstructed: r.reconstructed });
    if (r.from_id) edges.push({ from: r.from_id, to: r.node_id, via: r.via });
  }
  const origin = [...nodes.values()].find((n) => n.role === 'derived-claim');
  if (!origin) return null;
  const dfChildren = new Map();
  for (const e of edges) if (e.via === 'wasDerivedFrom') { (dfChildren.get(e.from) || dfChildren.set(e.from, []).get(e.from)).push(e.to); }
  const side = new Map(); const tier = new Map();
  const bfs = (rootId, tag) => {
    side.set(rootId, tag); tier.set(rootId, 0);
    let frontier = [rootId]; let t = 0; const seen = new Set([rootId]);
    while (frontier.length) {
      const next = [];
      for (const id of frontier) for (const c of (dfChildren.get(id) || [])) if (!seen.has(c)) { seen.add(c); side.set(c, tag); tier.set(c, t + 1); next.push(c); }
      frontier = next; t++;
    }
  };
  bfs(origin.id, 'L');
  const contradictors = [...nodes.values()].filter((n) => n.role === 'contradicting-claim');
  for (const cn of contradictors) if (!side.has(cn.id)) bfs(cn.id, 'R');
  for (const n of nodes.values()) if (!side.has(n.id)) { side.set(n.id, 'L'); tier.set(n.id, 1); }   // safety: never drop a node
  const hasRight = contradictors.length > 0;
  const groups = {};
  for (const n of nodes.values()) { const k = side.get(n.id) + ':' + tier.get(n.id); (groups[k] || (groups[k] = [])).push(n.id); }
  const lenOf = (pred) => Object.entries(groups).filter(([k]) => pred(k)).map(([, v]) => v.length);
  const leftCols = Math.max(1, ...lenOf((k) => k[0] === 'L'));
  const rightCols = hasRight ? Math.max(1, ...lenOf((k) => k[0] === 'R')) : 0;
  const maxTier = Math.max(...[...tier.values()]);
  const { NODE_W, NODE_H, H_GAP, V_GAP, MARGIN, BAND_GAP } = DG;
  const leftBandW = leftCols * (NODE_W + H_GAP) - H_GAP;
  const rightBandW = hasRight ? rightCols * (NODE_W + H_GAP) - H_GAP : 0;
  const rightStart = MARGIN + leftBandW + (hasRight ? BAND_GAP : 0);
  const pos = new Map();
  for (const [k, ids] of Object.entries(groups)) {
    const s = k[0]; const t = Number(k.slice(2));
    const bandStart = s === 'L' ? MARGIN : rightStart;
    const bandW = s === 'L' ? leftBandW : rightBandW;
    const groupW = ids.length * (NODE_W + H_GAP) - H_GAP;
    const startX = bandStart + Math.max(0, (bandW - groupW) / 2);
    ids.forEach((id, i) => pos.set(id, { x: startX + i * (NODE_W + H_GAP), y: MARGIN + t * (NODE_H + V_GAP) }));
  }
  const width = rightStart + (hasRight ? rightBandW : 0) + MARGIN;
  const height = MARGIN * 2 + (maxTier + 1) * (NODE_H + V_GAP) - V_GAP;
  return { nodes: [...nodes.values()], edges, pos, width, height };
}

function DerivationDiagram({ trace, activeId, onPick }) {
  const layout = computeDerivationLayout(trace);
  if (!layout) return null;
  const { NODE_W, NODE_H } = DG;
  const fill = (n) => n.role === 'derived-claim' ? 'var(--blue)' : n.role === 'contradicting-claim' ? '#FBEAE8' : 'var(--warm-white)';
  const stroke = (n) => n.id === activeId ? 'var(--orange)' : n.role === 'contradicting-claim' ? 'var(--err)' : 'var(--border)';
  const textCol = (n) => n.role === 'derived-claim' ? '#fff' : n.role === 'contradicting-claim' ? 'var(--err)' : 'var(--ink)';
  return (
    <div className="matrix-scroll">
      <svg width={layout.width} height={layout.height} role="img"
           style={{ maxWidth: 'none', fontFamily: 'inherit' }}>
        {layout.edges.map((e, i) => {
          const a = layout.pos.get(e.from), b = layout.pos.get(e.to);
          if (!a || !b) return null;
          const contra = e.via === 'contradicts';
          if (contra) {
            const x1 = a.x + NODE_W, y1 = a.y + NODE_H / 2, x2 = b.x, y2 = b.y + NODE_H / 2;
            const mx = (x1 + x2) / 2;
            return (
              <g key={i}>
                <path d={`M${x1},${y1} C${mx},${y1} ${mx},${y2} ${x2},${y2}`} fill="none" stroke="var(--err)" strokeWidth="2" strokeDasharray="5 3" />
                <text x={mx} y={Math.min(y1, y2) - 6} textAnchor="middle" fontSize="10" fill="var(--err)">contradicts</text>
              </g>
            );
          }
          const x1 = a.x + NODE_W / 2, y1 = a.y + NODE_H, x2 = b.x + NODE_W / 2, y2 = b.y;
          const my = (y1 + y2) / 2;
          return <path key={i} d={`M${x1},${y1} C${x1},${my} ${x2},${my} ${x2},${y2}`} fill="none" stroke="var(--muted)" strokeWidth="1.5" />;
        })}
        {layout.nodes.map((n) => {
          const p = layout.pos.get(n.id); if (!p) return null;
          return (
            <g key={n.id} transform={`translate(${p.x},${p.y})`} style={{ cursor: 'pointer' }} onClick={() => onPick(n.id)}>
              <title>{`${n.title}\n(${n.id}) — ${n.role}`}</title>
              <rect width={NODE_W} height={NODE_H} rx="7" fill={fill(n)} stroke={stroke(n)} strokeWidth={n.id === activeId ? 2.5 : 1.5} />
              <text x="10" y="19" fontSize="11" fontWeight="600" fill={textCol(n)}>{truncate(n.title, 24)}</text>
              <text x="10" y="35" fontSize="9" fill={n.role === 'derived-claim' ? '#CBD8EC' : 'var(--muted)'}>
                {n.role === 'derived-claim' ? 'derived claim' : n.role === 'contradicting-claim' ? 'opposing claim' : 'source'}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

function DerivationGraph({ health, onEndorsed }) {
  const { getAccessTokenSilently } = useAuth0();
  const deriv = health && health.derivations;
  const recent = (deriv && deriv.recent) || [];
  const firstId = recent.length ? recent[0].note_id : null;   // primitive → stable effect dep (array identity is not)
  const [activeId, setActiveId] = useState(null);
  const [trace, setTrace] = useState(undefined);   // undefined=idle/loading, null=error, {...}
  const [err, setErr] = useState('');
  const [endorsing, setEndorsing] = useState(false);

  const traceId = useCallback(async (id) => {
    try {
      const r = await gatewayCall(getAccessTokenSilently, 'knowledge.derivation_trace', { note_id: id });
      setTrace(r);
    } catch (e) { setTrace(null); setErr(e.message); }
  }, [getAccessTokenSilently]);

  useEffect(() => {
    if (!activeId) { setTrace(undefined); return; }
    let live = true;
    setTrace(undefined); setErr('');
    (async () => {
      try {
        const r = await gatewayCall(getAccessTokenSilently, 'knowledge.derivation_trace', { note_id: activeId });
        if (live) setTrace(r);
      } catch (e) { if (live) { setTrace(null); setErr(e.message); } }
    })();
    return () => { live = false; };
  }, [activeId, getAccessTokenSilently]);

  // The King endorses a confirmed claim into a `conclusion` — his OWN act, with his own token (a), not the
  // machine-tier confirmer. On success, re-trace so the badge flips to King-endorsed and bump the parent.
  const endorse = async () => {
    if (!activeId) return;
    setEndorsing(true); setErr('');
    try {
      await gatewayCall(getAccessTokenSilently, 'knowledge.endorse_claim', { note_id: activeId });
      await traceId(activeId);
      if (onEndorsed) onEndorsed();
    } catch (e) { setErr(e.message); }
    finally { setEndorsing(false); }
  };

  // Auto-open the newest derived claim once the list arrives, so the King lands on a picture, not a prompt.
  useEffect(() => { if (!activeId && firstId) setActiveId(firstId); }, [firstId, activeId]);

  return (
    <section className="section">
      <h2>Derivation graph <span className="modal-sub">— how a claim was reached</span></h2>
      <p className="section-intro">
        The knowledge base does not just hold notes; it records how each <strong>synthesised claim</strong> was
        derived — the sources it was built from, and any claim it <strong>contradicts</strong>. Pick a claim to
        see its provenance as a picture: sources fan out below it, and an opposing claim (if one has been
        asserted) sits across a red edge with its own sources. Click any node to walk the chain from there.
      </p>

      {health === undefined ? <p className="modal-note">Loading…</p>
        : (!deriv || deriv.available === false) ? (
          <p className="modal-note">The derivation layer is not reporting yet{deriv && deriv.note ? ` (${deriv.note})` : ''}.</p>
        ) : deriv.total === 0 ? (
          <p className="modal-note">
            The derivation layer is <strong>live but empty</strong> — no synthesis has been approved yet, so there is
            nothing to trace. As the Council approves syntheses and asserts contradictions, claims will appear here to
            explore. <span className="modal-sub">(This is the honest current state, not an error — the machine is ready and waiting for its first derived claim.)</span>
          </p>
        ) : (
          <>
            <div className="stats" style={{ marginBottom: 12 }}>
              <div className="stat-card"><h3>Derived claims</h3><div className="stat-value">{deriv.total}</div></div>
              <div className="stat-card"><h3>King-endorsed</h3><div className="stat-value">{deriv.conclusions_total ?? 0}<span className="modal-sub"> conclusion{(deriv.conclusions_total ?? 0) === 1 ? '' : 's'}</span></div></div>
              <div className="stat-card"><h3>Contradictions</h3><div className="stat-value">{deriv.contradictions_total}</div></div>
            </div>

            <div className="matrix-scroll">
              <table className="data-table">
                <thead><tr><th>Derived claim</th><th>Status</th><th>Sources</th><th>Contradiction</th><th>Ontology</th><th>By</th></tr></thead>
                <tbody>
                  {recent.map((r) => (
                    <tr key={r.note_id} className="clickable" onClick={() => setActiveId(r.note_id)}
                        style={{ background: r.note_id === activeId ? '#DCE7F5' : undefined }}>
                      <td title={r.title}><strong>{truncate(r.title, 56)}</strong></td>
                      <td><ClaimStatus status={r.status} /></td>
                      <td>{r.source_count}</td>
                      <td>{r.reaches_contradiction ? <span style={{ color: 'var(--err)' }}>⚡ yes</span> : <span className="modal-sub">—</span>}</td>
                      <td className="modal-sub">{r.provenance && r.provenance.ontology_version}</td>
                      <td className="modal-sub">{r.created_by}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {activeId && (
              <div style={{ marginTop: 16 }}>
                {trace === undefined ? <p className="modal-note">Tracing…</p>
                  : trace === null ? <p className="cfg-err">Could not trace this claim: {err}</p>
                  : (
                    <>
                      <p className="modal-oc">
                        <ClaimStatus status={trace.status} />
                        {trace.endorsement && <span className="modal-sub"> — endorsed by {trace.endorsement.endorsed_by}</span>}
                        {' · '}<strong>{trace.source_count}</strong> source{trace.source_count === 1 ? '' : 's'}
                        {trace.reaches_contradiction
                          ? <> · reaches an <strong style={{ color: 'var(--err)' }}>opposing claim</strong></>
                          : <> · no contradiction asserted against it yet</>}
                        {trace.provenance && <> · <span className="modal-sub">reached by {trace.provenance.method} / {trace.provenance.model} ({trace.provenance.ontology_version})</span></>}
                        {trace.truncated && <> · <span className="modal-sub">(view truncated at the traversal bound)</span></>}
                      </p>
                      <DerivationDiagram trace={trace.trace} activeId={activeId} onPick={setActiveId} />
                      <p className="modal-note">
                        Blue = the claim you are tracing · grey-bordered = a source it was derived from · red = an opposing claim.
                        Hover a box for its full text, or read them all below · click any node to trace from there.
                      </p>
                      {/* Full text of every box — the diagram truncates, so read the whole claim/source here. */}
                      <div className="matrix-scroll">
                        <table className="data-table">
                          <thead><tr><th>Role</th><th>Full text</th></tr></thead>
                          <tbody>
                            {Array.from(new Map((trace.trace || []).map((n) => [n.node_id, n])).values())
                              .sort((a, b) => (a.role === 'derived-claim' ? -1 : b.role === 'derived-claim' ? 1
                                : a.role === 'contradicting-claim' ? 1 : b.role === 'contradicting-claim' ? -1 : 0))
                              .map((n) => (
                                <tr key={n.node_id} className="clickable" onClick={() => setActiveId(n.node_id)}
                                    style={{ background: n.node_id === activeId ? '#DCE7F5' : undefined }}>
                                  <td className="modal-sub" style={{ whiteSpace: 'nowrap' }}>
                                    {n.role === 'derived-claim' ? 'claim' : n.role === 'contradicting-claim' ? 'opposing claim' : 'source'}
                                  </td>
                                  <td>{n.title}</td>
                                </tr>
                              ))}
                          </tbody>
                        </table>
                      </div>
                      {trace.status === 'confirmed' && (
                        <p className="modal-oc">
                          <button className="cfg-save" disabled={endorsing} onClick={endorse}>
                            {endorsing ? 'Endorsing…' : '♛ Endorse as conclusion'}
                          </button>{' '}
                          <span className="modal-sub">Your own royal act — lifts this machine-confirmed claim to a King-endorsed conclusion.</span>
                          {err && <><br /><span className="cfg-err">{err}</span></>}
                        </p>
                      )}
                      {trace.status === 'conclusion' && (
                        <p className="modal-oc"><ClaimStatus status="conclusion" /> — you have endorsed this claim.</p>
                      )}
                    </>
                  )}
              </div>
            )}
          </>
        )}
    </section>
  );
}

// D-230 step 6 — the verdict surface (W1). A ranked queue of the Librarian's pending deductions; the King
// confirms (machine-tier, via the ROLE-CONSOLE-CONFIRM backend identity — NOT his own) or rejects. Confirm
// writes the claim + its derivation; the claim then appears in the graph below, where it can be endorsed
// into a conclusion (the King's own act). Minimal by design (D-230): one list, one gesture each.
function PendingDeductions({ onResolved }) {
  const { getAccessTokenSilently } = useAuth0();
  const [items, setItems] = useState(undefined);   // undefined=loading, null=unavailable, []
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState('');
  const [openId, setOpenId] = useState(null);      // which draft is expanded to its full text

  const load = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      const r = await axios.get(`${API}/knowledge/deductions/pending`, { headers: { Authorization: `Bearer ${token}` } });
      const list = Array.isArray(r.data) ? r.data : (r.data?.proposals || r.data?.pending || []);
      setItems(list); setErr('');
    } catch (e) { setItems(null); setErr(e.response?.data?.detail || e.message); }
  }, [getAccessTokenSilently]);

  useEffect(() => { load(); }, [load]);

  const resolve = async (id, action) => {
    setBusy(id); setErr('');
    try {
      const token = await getAccessTokenSilently();
      await axios.post(`${API}/knowledge/deductions/${id}/resolve`, { action }, { headers: { Authorization: `Bearer ${token}` } });
      await load();
      if (onResolved) onResolved();
    } catch (e) { setErr(e.response?.data?.detail || e.message); }
    finally { setBusy(''); }
  };

  return (
    <section className="section">
      <h2>Pending deductions <span className="modal-sub">— your verdict</span></h2>
      <p className="section-intro">
        Candidate claims the Librarian has drafted from the knowledge base, awaiting your verdict.
        <strong> Confirm</strong> writes the claim and its provenance — machine-tier, recorded by the console
        confirmer, <em>not</em> as your endorsement; <strong>reject</strong> discards it. Nothing durable is
        written until you confirm (D-039). A confirmed claim can then be <strong>endorsed</strong> into a
        conclusion in the graph below — that one is your own royal act.
      </p>
      {items === undefined ? <p className="modal-note">Loading the verdict queue…</p>
        : items === null ? <p className="modal-note">The verdict queue is unavailable{err ? ` (${err})` : ''} — it needs the confirmer identity (ROLE-CONSOLE-CONFIRM) provisioned and its seed applied.</p>
        : items.length === 0 ? <p className="modal-note">No pending deductions — the Librarian has nothing awaiting your verdict right now.</p>
        : (
          <>
            <div className="matrix-scroll">
              <table className="data-table">
                <thead><tr><th>Draft claim</th><th>Sources</th><th>Model</th><th>Verdict</th></tr></thead>
                <tbody>
                  {items.map((p) => {
                    const open = openId === p.id;
                    const long = (p.title && p.title.length > 64) || (p.summary && p.summary.length > 140);
                    return (
                      <tr key={p.id}>
                        <td style={{ maxWidth: 640 }} title={`${p.title}\n\n${p.summary}`}>
                          <strong style={{ whiteSpace: open ? 'normal' : 'nowrap' }}>{open ? p.title : truncate(p.title, 64)}</strong>
                          <br />
                          <span className="modal-sub" style={{ whiteSpace: 'pre-wrap' }}>{open ? p.summary : truncate(p.summary, 140)}</span>
                          {long && (
                            <> <span className="clickable" style={{ color: 'var(--blue)', fontWeight: 600 }}
                                     onClick={() => setOpenId(open ? null : p.id)}>{open ? '· show less' : '· read full'}</span></>
                          )}
                        </td>
                        <td>{p.source_count}</td>
                        <td className="modal-sub">{p.model}</td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          <button className="cfg-save" disabled={busy === p.id} onClick={() => resolve(p.id, 'approve')}>{busy === p.id ? '…' : 'Confirm'}</button>{' '}
                          <button className="cfg-cancel" disabled={busy === p.id} onClick={() => resolve(p.id, 'reject')}>Reject</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {err && <p className="cfg-err">{err}</p>}
          </>
        )}
    </section>
  );
}

function KnowledgeBase() {
  const { getAccessTokenSilently } = useAuth0();
  const [ov, setOv] = useState(undefined);        // undefined=loading, null=error, {...}
  const [health, setHealth] = useState(undefined);
  const [err, setErr] = useState('');
  const [refreshTick, setRefreshTick] = useState(0);   // bumped after a verdict so the figures + graph refresh
  const bump = () => setRefreshTick((t) => t + 1);

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const token = await getAccessTokenSilently();
        const r = await axios.get(`${API}/kingdom/knowledge`, { headers: { Authorization: `Bearer ${token}` } });
        if (live) setOv(r.data);
      } catch (e) { if (live) setOv(null); }
      try {
        const h = await gatewayCall(getAccessTokenSilently, 'knowledge.health', {});
        if (live) setHealth(h);
      } catch (e) { if (live) { setHealth(null); setErr(`Live figures unavailable: ${e.message}`); } }
    })();
    return () => { live = false; };
  }, [getAccessTokenSilently, refreshTick]);

  if (ov === undefined) return <div className="loading"><h2>Loading the knowledge base…</h2></div>;
  if (ov === null) return <section className="section"><p className="cfg-err">Could not load the knowledge-base overview.</p></section>;

  const notes = health?.notes;
  const byAuthor = notes?.by_author || [];
  const noise = health?.noise_summary;

  return (
    <>
      {/* 1 — what this thing IS, before any number */}
      <section className="section">
        <h2>The Knowledge Base <span className="modal-sub">— {ov.intro.headline}</span></h2>
        <p className="section-intro">{ov.intro.body}</p>
        <div className="cap-grid">
          {ov.intro.points.map((p) => (
            <div className="cap-card" key={p.h}>
              <div className="cap-card-name">{p.h}</div>
              <div className="cap-card-summary">{p.body}</div>
            </div>
          ))}
        </div>
      </section>

      {/* 2 — the headline figures */}
      <div className="stats">
        <div className="stat-card"><h3>Notes</h3><div className="stat-value">{notes?.total ?? '—'}</div></div>
        <div className="stat-card"><h3>Registries</h3><div className="stat-value">{ov.stores.length}</div></div>
        <div className="stat-card"><h3>Newest note</h3><div className="stat-value">{dateOnly(notes?.newest)}</div></div>
        <div className="stat-card"><h3>Redundancy</h3><div className="stat-value">{noise ? `${noise.noise_pct}%` : '—'}</div></div>
      </div>
      {err && <p className="modal-note">{err} — the structure below is still accurate; only the live figures are missing.</p>}

      {/* 3 — what is actually stored, with live sizes */}
      <section className="section">
        <h2>What is in it</h2>
        <p className="section-intro">
          {ov.stores.length} registries across {new Set(ov.stores.map((s) => s.engine.split(' ')[0])).size} engines.
          A <strong>fenced</strong> registry is one the Janitor may never
          touch and only the King may write — that is how the kingdom's law is protected from its own housekeeping.
        </p>
        <div className="matrix-scroll">
          <table className="data-table">
            <thead><tr><th>Registry</th><th>Records</th><th>Engine</th><th>What it holds</th><th>Who writes it</th></tr></thead>
            <tbody>
              {ov.stores.map((s) => {
                const c = storeCount(s, health);
                return (
                  <tr key={s.id}>
                    <td><strong>{s.name}</strong>{s.fenced && <> <span className="modal-sub">· fenced</span></>}</td>
                    <td>{c == null ? '—' : c}</td>
                    <td className="modal-sub">{s.engine}</td>
                    <td>{s.holds}</td>
                    <td className="modal-sub">{s.written_by}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      {/* 4 — freshness and who is actually feeding it */}
      <section className="section">
        <h2>How fresh, and who feeds it</h2>
        {health === undefined ? <p className="modal-note">Loading live figures…</p>
          : health === null ? <p className="cfg-err">{err}</p>
          : (
            <>
              <p className="modal-oc">
                <strong>Span:</strong> {dateOnly(notes?.oldest)} → {dateOnly(notes?.newest)}<br />
                <strong>Redundancy:</strong> {noise?.removable_notes_estimate ?? 0} notes removable of {notes?.total ?? 0} ({noise?.noise_pct ?? 0}%)
                — {notes?.exact_duplicate_groups ?? 0} exact-duplicate groups, {notes?.empty_or_tiny ?? 0} empty or tiny.<br />
                <span className="modal-sub">The acquis is fenced and never counted as noise.</span>
              </p>
              <h3>Notes by author</h3>
              {byAuthor.length === 0 ? <p className="modal-note">No authorship recorded yet.</p> : (
                <div className="matrix-scroll">
                  <table className="data-table">
                    <thead><tr><th>Author</th><th>Notes</th></tr></thead>
                    <tbody>
                      {byAuthor.map((a) => (
                        <tr key={a.author || 'unknown'}><td>{a.author || 'Unknown'}</td><td>{a.count}</td></tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
      </section>

      {/* 5 — the access model: the question "who can read or write this?" answered explicitly */}
      <section className="section">
        <h2>Who can read and write</h2>
        <p className="section-intro">
          Reading, writing and curating are three separate powers, granted separately. Every call arrives at the
          gateway carrying an identity, and the gateway checks the power before it touches a store.
        </p>
        <div className="matrix-scroll">
          <table className="data-table">
            <thead><tr><th>Power</th><th>Scope</th><th>What it permits</th><th>Tools</th></tr></thead>
            <tbody>
              {ov.scopes.map((s) => (
                <tr key={s.scope}>
                  <td><strong>{s.verb}</strong></td>
                  <td><code>{s.scope}</code></td>
                  <td>{s.permits}</td>
                  <td className="modal-sub">{s.tools.join(', ')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <h3>Who holds them</h3>
        <div className="matrix-scroll">
          <table className="data-table">
            <thead><tr><th>Identity</th><th>Read</th><th>Write</th><th>Curate</th><th>Notes</th></tr></thead>
            <tbody>
              {ov.holders.map((h) => (
                <tr key={h.who}>
                  <td><strong>{h.who}</strong><br /><span className="modal-sub">{h.id}</span></td>
                  <td>{h.read ? '✓' : '·'}</td>
                  <td>{h.write ? '✓' : '·'}</td>
                  <td>{h.curate ? '✓' : '·'}</td>
                  <td className="modal-sub">{h.note}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="modal-note">{ov.caveat}</p>
      </section>

      {/* 6 — the verdict surface: confirm/reject the Librarian's pending deductions (D-230 step 6) */}
      <PendingDeductions onResolved={bump} />

      {/* 7 — the derivation graph: how a claim was reached (B-127, D-219(5)); endorse confirmed claims here */}
      <DerivationGraph health={health} onEndorsed={bump} />

      {/* 8 — hygiene, promoted out of the agent card it used to hide behind */}
      <JanitorPanel />
    </>
  );
}

function App() {
  const { isAuthenticated, isLoading, user, loginWithRedirect, logout, getAccessTokenSilently, getIdTokenClaims, error: auth0Error } = useAuth0();
  const [isAdmin, setIsAdmin] = useState(false);
  const [checking, setChecking] = useState(true);
  const [denied, setDenied] = useState(false);   // authenticated but NOT authorized — terminal state (no auto re-login)
  const [activeTab, setActiveTab] = useState('console');
  const [detail, setDetail] = useState(null);   // {kind:'contract'|'instance'|'agent', data}
  const [agentPacket, setAgentPacket] = useState(null);  // {available, packet|reason} for the selected agent
  const [loading, setLoading] = useState(false);
  const [selectedNamespace, setSelectedNamespace] = useState('all');
  const [data, setData] = useState({
    namespaces: [], pods: [], deployments: [], services: [], ingresses: [],
    court: [], capabilityRoles: [], placements: [], matrix: { contracts: [], rows: [] }, registry: {},
    auditReports: [],
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const token = await getAccessTokenSilently();
      const h = { headers: { Authorization: `Bearer ${token}` } };
      const get = (p) => axios.get(`${API}${p}`, h).then((r) => r.data).catch(() => ({}));
      // Agent registry (gateway, King token → ROLE-KING) — powers the lifecycle dots on the court
      // cards (nomination/active stages). Resilient: [] on any failure so the court still renders.
      const regFetch = fetch(GATEWAY_MCP, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ tool: 'registry.list', input: {} }),
      }).then((r) => r.json()).then((d) => (d && d.result && d.result.agents) || []).catch(() => []);
      const [ns, pods, deps, svcs, ings, court, caps, matrix, audit, regAgents] = await Promise.all([
        get('/platform/kubernetes/namespaces'),
        get('/platform/kubernetes/pods'),
        get('/platform/kubernetes/deployments'),
        get('/platform/kubernetes/services'),
        get('/platform/kubernetes/ingresses'),
        get('/kingdom/court'),
        get('/kingdom/capabilities'),
        get('/kingdom/contracts'),
        get('/kingdom/audit/reports'),
        regFetch,
      ]);
      const registry = {};
      for (const a of regAgents) registry[a.agent_id] = a;
      setData({
        namespaces: ns.namespaces || [], pods: pods.pods || [], deployments: deps.deployments || [],
        services: svcs.services || [], ingresses: ings.ingresses || [],
        court: court.court || [], courtDegraded: court.degraded || false, courtReason: court.reason || null,
        courtGeneratedAt: court.generated_at || null, courtStale: court.stale || false,
        capabilityRoles: caps.capability_roles || [], placements: caps.placements || [],
        matrix: { contracts: matrix.contracts || [], rows: matrix.rows || [] }, registry,
        auditReports: audit.entries || [],
      });
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently]);

  const checkAuth = useCallback(async () => {
    try {
      if (isLoading) return;
      // Auth0 denied the login itself (the Kingdom Post-Login Action is deny-by-default: an
      // unprovisioned account gets NO token and returns as ?error=access_denied). There is no
      // token and never will be for this account, so do NOT loginWithRedirect() again — that is
      // the infinite loop. Land on the terminal deny screen (which surfaces the Auth0 message).
      if (auth0Error) {
        setDenied(true);
        setChecking(false);
        return;
      }
      if (isAuthenticated) {
        const claims = await getIdTokenClaims();
        const roles = claims['https://onmygarage.eu/roles'] || [];
        if (!roles.includes('admin')) {
          // Authenticated but NOT authorized. Do NOT auto-logout-then-relogin: the IdP (Google)
          // session is still live, so loginWithRedirect() would silently re-auth the SAME account
          // and loop forever. Land on a terminal deny with an explicit way out (federated sign-out).
          setDenied(true);
          setChecking(false);
          return;
        }
        setIsAdmin(true);
        setChecking(false);
        load();
      } else {
        await loginWithRedirect();
      }
    } catch (error) {
      console.error('Auth error:', error);
      setChecking(false);
    }
  }, [auth0Error, getIdTokenClaims, isAuthenticated, isLoading, load, loginWithRedirect]);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  // When an agent with an introspection endpoint is selected, lazy-load its loaded packet.
  useEffect(() => {
    if (detail?.kind === 'agent' && detail.data?.has_packet) {
      setAgentPacket(null);
      (async () => {
        try {
          const token = await getAccessTokenSilently();
          const r = await axios.get(`${API}/kingdom/agent/${detail.data.id}/packet`, { headers: { Authorization: `Bearer ${token}` } });
          setAgentPacket(r.data);
        } catch (e) {
          setAgentPacket({ available: false, reason: 'request failed' });
        }
      })();
    } else {
      setAgentPacket(null);
    }
  }, [detail, getAccessTokenSilently]);

  if (isLoading || checking) {
    return <div className="App"><div className="loading"><h2>Checking admin access…</h2><p>Verifying MFA and admin role…</p></div></div>;
  }
  if (denied || !isAdmin) {
    return (
      <div className="App"><div className="error">
        <h2>Access Denied</h2>
        <p>{auth0Error?.message || 'This account is not authorized for the console. An admin role (and MFA) is required.'}</p>
        <button className="logout-btn" onClick={() => logout({ logoutParams: { returnTo: window.location.origin, federated: true } })}>
          Sign out &amp; use a different account
        </button>
      </div></div>
    );
  }

  const nsFilter = (rows) => selectedNamespace === 'all' ? rows : rows.filter((r) => r.namespace === selectedNamespace);
  const filteredPods = nsFilter(data.pods);
  const filteredDeployments = nsFilter(data.deployments);

  // org tree: map children by reports_to, find roots (reports_to == null)
  const childrenOf = {};
  data.court.forEach((n) => { if (n.reports_to) (childrenOf[n.reports_to] = childrenOf[n.reports_to] || []).push(n); });
  const roots = data.court.filter((n) => !n.reports_to);
  // Kingdom Council seats — ministers that hold (or reserve) a seat, ordered by seat index (D-161/D-171).
  const councilSeats = data.court
    .filter((n) => n.council_seat || n.council_seat_status)
    .sort((a, b) => (a.council_seat_index || 99) - (b.council_seat_index || 99));

  return (
    <div className="App">
      <header className="App-header">
        <div className="brand">
          <img className="brand-emblem" src="/living-archive-seal-256.png" alt="The Living Archive" />
          <div>
            <h1>Kingdom Console</h1>
            <div className="brand-sub">The Living Archive</div>
          </div>
        </div>
        <div className="user-info">
          <span className="user-name">{user?.name || user?.email}</span>
          <span className="user-role">Admin</span>
          <button onClick={handleLogoutFactory(logout)} className="logout-btn">Logout</button>
        </div>
      </header>

      <nav className="tabs">
        {TABS.map((t) => (
          <button key={t.id} className={`tab ${activeTab === t.id ? 'tab-active' : ''}`} onClick={() => setActiveTab(t.id)}>
            {t.label}
          </button>
        ))}
        <button onClick={load} disabled={loading} className="refresh-btn tabs-refresh">{loading ? 'Loading…' : 'Refresh'}</button>
      </nav>

      <main className="dashboard">
        {activeTab === 'briefing' && <SurfaceRenderer surfaceId="king.home" />}
        {activeTab === 'console' && (
          <>
            <div className="controls">
              <select value={selectedNamespace} onChange={(e) => setSelectedNamespace(e.target.value)} className="namespace-select">
                <option value="all">All Namespaces</option>
                {data.namespaces.map((ns) => <option key={ns.name} value={ns.name}>{ns.name}</option>)}
              </select>
            </div>
            <div className="stats">
              <div className="stat-card"><h3>Namespaces</h3><div className="stat-value">{data.namespaces.length}</div></div>
              <div className="stat-card"><h3>Pods</h3><div className="stat-value">{filteredPods.length}</div></div>
              <div className="stat-card"><h3>Deployments</h3><div className="stat-value">{filteredDeployments.length}</div></div>
              <div className="stat-card"><h3>Public hosts</h3><div className="stat-value">{data.ingresses.reduce((a, i) => a + (i.hosts?.length || 0), 0)}</div></div>
            </div>

            <section className="section">
              <h2>Public hosts (ingress)</h2>
              <table className="data-table">
                <thead><tr><th>Host</th><th>Namespace</th><th>Ingress</th><th>TLS</th></tr></thead>
                <tbody>
                  {data.ingresses.flatMap((ing) => (ing.hosts || []).map((host) => (
                    <tr key={`${ing.namespace}-${host}`}>
                      <td>{host}</td><td>{ing.namespace}</td><td>{ing.name}</td>
                      <td className={`status status-${ing.tls ? 'ok' : 'err'}`}>{ing.tls ? 'TLS' : 'none'}</td>
                    </tr>
                  )))}
                  {data.ingresses.length === 0 && <tr><td colSpan="4" className="empty">No ingresses</td></tr>}
                </tbody>
              </table>
            </section>

            <section className="section">
              <h2>Pods {selectedNamespace !== 'all' && `(${selectedNamespace})`}</h2>
              <table className="data-table">
                <thead><tr><th>Name</th><th>Namespace</th><th>Status</th><th>Ready</th><th>Node</th></tr></thead>
                <tbody>
                  {filteredPods.map((pod) => (
                    <tr key={`${pod.namespace}-${pod.name}`}>
                      <td>{pod.name}</td><td>{pod.namespace}</td>
                      <td className={`status status-${pod.status?.toLowerCase() === 'running' ? 'ok' : pod.status?.toLowerCase() === 'pending' ? 'warn' : 'err'}`}>{pod.status}</td>
                      <td>{pod.ready}/{pod.total_containers}</td><td>{pod.node}</td>
                    </tr>
                  ))}
                  {filteredPods.length === 0 && <tr><td colSpan="5" className="empty">No pods found</td></tr>}
                </tbody>
              </table>
            </section>

            <section className="section">
              <h2>Deployments {selectedNamespace !== 'all' && `(${selectedNamespace})`}</h2>
              <table className="data-table">
                <thead><tr><th>Name</th><th>Namespace</th><th>Replicas</th><th>Ready</th></tr></thead>
                <tbody>
                  {filteredDeployments.map((dep) => (
                    <tr key={`${dep.namespace}-${dep.name}`}>
                      <td>{dep.name}</td><td>{dep.namespace}</td><td>{dep.replicas}</td>
                      <td className={`status status-${dep.ready_replicas === dep.replicas ? 'ok' : 'warn'}`}>{dep.ready_replicas}/{dep.replicas}</td>
                    </tr>
                  ))}
                  {filteredDeployments.length === 0 && <tr><td colSpan="4" className="empty">No deployments found</td></tr>}
                </tbody>
              </table>
            </section>
          </>
        )}

        {activeTab === 'court' && (
          <section className="section">
            <h2>Kingdom Court</h2>
            {data.courtDegraded && (
              <div role="alert" style={{ background: '#B8791C1a', border: '1px solid #B8791C', color: '#8a5a10', borderRadius: '8px', padding: '.6rem .9rem', margin: '.5rem 0', fontSize: '.9rem' }}>
                ⚠ <strong>Degraded view — static fallback.</strong> The live acquis registry was unreachable, so the agents and statuses below are the built-in list and may be stale.{data.courtReason ? ` (${data.courtReason})` : ''}
              </div>
            )}
            {councilSeats.length > 0 && (
              <div className="council-banner">
                <div className="council-banner-head">
                  <span className="council-crest" aria-hidden="true">⚜</span>
                  <p>
                    <strong>The Kingdom Council is forming.</strong> The King's deliberative body of ministers — one shared knowledge base and thread, each minister a persona <em>lens</em> over the shared council runtime (a seat, not a separate agent; D-161/D-171). The model behind a seat is a swappable engine (Opus&nbsp;5 default), never welded to the office.
                  </p>
                </div>
                <ul className="council-seats">
                  {councilSeats.map((s) => {
                    const reserved = s.council_seat_status === 'reserved';
                    return (
                      <li key={s.id} className={`council-seat${reserved ? ' council-seat-reserved' : ''}`}>
                        <span className="council-seat-dot" aria-hidden="true" />
                        <span className="council-seat-name">{s.name}</span>
                        <span className="council-seat-meta">
                          {s.council_seat_index ? `seat ${s.council_seat_index} · ` : ''}{reserved ? 'reserved' : 'active'}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
            <p className="section-intro">The kingdom's population and chain of command. Internal agents run in-cluster; external advisors (Nimbus, Strategist) operate outside it. Status is live <em>when this banner is absent</em>.</p>
            <p className="section-intro">Click any agent for its runtime detail — scopes, model, and what it loaded at start. The <strong>Knowledge Janitor</strong> (under the Minister of Knowledge) opens its hygiene view with the finesse control.</p>
            {data.courtGeneratedAt && (
              <p className="court-asof">
                Roster as of {new Date(data.courtGeneratedAt).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}
                {data.courtStale ? ' · roster-sync may be behind (the list below is the acquis roster, not a fallback)' : ''}.
              </p>
            )}
            <div className="court-scroll">
              <ul className="court-tree court-root">
                {roots.map((r) => <CourtNode key={r.id} node={r} childrenOf={childrenOf} registry={data.registry} onSelect={(n) => setDetail({ kind: 'agent', data: n })} />)}
              </ul>
            </div>
            {data.court.length === 0 && <p className="empty">Catalogue unavailable.</p>}
          </section>
        )}

        {activeTab === 'knowledge' && <KnowledgeBase />}

        {activeTab === 'audit' && (
          <section className="section">
            <h2>Auditor (Echo) — findings &amp; logs</h2>
            <p className="section-intro">Echo's full audit logs, read straight from <code>kingdom-audit-reports</code> — the King-direct channel. Independent of Alfred (PRIN-010): the auditor reports here, the King reads here. Each run carries its findings AND the deterministic platform snapshot (what is actually deployed &amp; running), token usage, and the weekly LLM deep-review. Expand <em>Raw log</em> for the complete record. Newest first.</p>
            {data.auditReports.length === 0 && <p className="empty">No reports available. (Echo runs daily; a clean run still writes a report with zero findings. If this stays empty, the read key may not be minted yet.)</p>}
            {data.auditReports.map((r) => {
              const findings = r.findings || [];
              const count = r.summary?.findings ?? findings.length;
              const worst = findings.some((x) => x.severity === 'critical') ? 'err' : count ? 'warn' : 'ok';
              return (
                <div key={r._key} className="section" style={{ marginBottom: '12px' }}>
                  <h3>
                    <span className={`status status-${worst}`}>{count ? `${count} finding(s)` : 'clean'}</span>
                    {' '}<span className="muted">{r.finished_at || r.started_at || r._key}</span>
                  </h3>
                  {findings.length > 0 && (
                    <table className="data-table">
                      <thead><tr><th>Severity</th><th>Check</th><th>Message</th></tr></thead>
                      <tbody>
                        {findings.map((x, i) => (
                          <tr key={i}>
                            <td className={`status status-${x.severity === 'critical' ? 'err' : x.severity === 'high' ? 'warn' : 'ok'}`}>{x.severity}</td>
                            <td>{x.check}</td>
                            <td>{x.message}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                  {/* Layer-1 platform snapshot — what is actually deployed & running (D-165). */}
                  {r.checks?.platform && !r.checks.platform.error && (
                    <div className="audit-snapshot">
                      <p className="muted" style={{ marginTop: '8px' }}>
                        <strong>Platform snapshot</strong>{' · '}
                        nodes {r.checks.platform.nodes?.ready ?? '?'}/{r.checks.platform.nodes?.total ?? '?'} ready{' · '}
                        deployments {r.checks.platform.deployments?.healthy ?? '?'}/{r.checks.platform.deployments?.total ?? '?'} healthy{' · '}
                        pods running {r.checks.platform.pods?.running ?? '?'}{' · '}
                        tokens {r.summary?.total_tokens ?? 0}{' · '}
                        spend €{r.summary?.spend_eur ?? 0}
                      </p>
                      {(r.checks.platform.deployments?.degraded || []).length > 0 && (
                        <p className="empty">Degraded: {r.checks.platform.deployments.degraded.map((d) => `${d.ns}/${d.name} (${d.ready}/${d.desired})`).join(', ')}</p>
                      )}
                      {(r.checks.platform.pods?.crashloops || []).length > 0 && (
                        <p className="empty">CrashLoop: {r.checks.platform.pods.crashloops.join(', ')}</p>
                      )}
                      {r.checks.platform.flux && !r.checks.platform.flux.error && (r.checks.platform.flux.not_ready || []).length > 0 && (
                        <p className="empty">Flux not reconciled: {r.checks.platform.flux.not_ready.map((f) => `${f.name} (${f.reason})`).join(', ')}</p>
                      )}
                    </div>
                  )}
                  {r.checks?.platform?.error && <p className="empty">Echo was BLIND to the live cluster this run: {r.checks.platform.error}</p>}
                  {/* Layer-2 weekly deep-review narrative. */}
                  {r.deep_review?.narrative && (
                    <div className="audit-deep" style={{ marginTop: '6px' }}>
                      <p style={{ marginBottom: '2px' }}><strong>Deep review</strong></p>
                      {/* LLM-written Markdown → rendered as formatted Markdown (headings/bold/lists), not raw text. */}
                      <Markdown text={r.deep_review.narrative} />
                      {(r.deep_review.watch_list || []).length > 0 && <p className="muted">Watch: {r.deep_review.watch_list.join(' · ')}</p>}
                    </div>
                  )}
                  {/* Raw log — the complete report record. */}
                  <details style={{ marginTop: '6px' }}>
                    <summary className="muted" style={{ cursor: 'pointer' }}>Raw log (full report JSON)</summary>
                    <pre style={{ maxHeight: '340px', overflow: 'auto', fontSize: '11px', padding: '8px', borderRadius: '4px', background: 'rgba(0,0,0,0.04)' }}>{JSON.stringify(r, null, 2)}</pre>
                  </details>
                  {r._error && <p className="empty">Unreadable report: {r._error}</p>}
                </div>
              );
            })}
          </section>
        )}

        {activeTab === 'capabilities' && (
          <section className="section">
            <h2>The two-axis agent model</h2>
            <p className="section-intro">
              Every agent's identity has two independent axes — <strong>Capability</strong> (what it does) ×
              <strong> Placement</strong> (where it sits and what authority its seat carries) — plus a <strong>Tier</strong> (T0–T3)
              that is <em>derived</em> from ring + exposure, never separately declared (D-120). Example: Alfred holds the
              <em> Orchestrator</em> capability at the <em>Prime-Minister</em> placement — a Minister, not an "advisor".
            </p>

            <h3>Capabilities <span className="axis-note">— what an agent does</span></h3>
            <div className="cap-grid">
              {data.capabilityRoles.map((c) => (
                <div key={c.id} className="cap-card">
                  <div className="cap-card-name">{c.name}</div>
                  <div className="cap-card-summary">{c.summary}</div>
                </div>
              ))}
            </div>

            <h3>Placements <span className="axis-note">— where an agent sits / its authority</span></h3>
            <div className="cap-grid">
              {(data.placements || []).map((pl) => (
                <div key={pl.id} className="cap-card">
                  <div className="cap-card-name">{pl.name}</div>
                  <div className="cap-card-summary">{pl.summary}</div>
                </div>
              ))}
            </div>

            {data.capabilityRoles.length === 0 && <p className="empty">Catalogue unavailable.</p>}
          </section>
        )}

        {activeTab === 'tiers' && (
          <section className="section">
            <h2>Capabilities &amp; Contracts</h2>
            <p className="section-intro">
              The one authoritative view: every <strong>resource</strong> the platform has, the <strong>contract</strong> it's offered through (columns — click for how it works), and the <strong>component</strong> that offers or uses it (rows — click for detail), with the exact <strong>right</strong> each holds. <strong>T3</strong> (Nimbus / operator) <em>offers</em> the contracts; everything else <em>uses</em> them — only through the gateway. Status is live.
            </p>
            <div className="matrix-scroll">
              <table className="data-table matrix">
                <thead>
                  <tr>
                    <th>Instance</th><th>Tier</th>
                    {data.matrix.contracts.map((c) => (
                      <th key={c.id} className="clickable" title="Click: how this contract works" onClick={() => setDetail({ kind: 'contract', data: c })}>{c.name}</th>
                    ))}
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {data.matrix.rows.map((r) => (
                    <tr key={r.name}>
                      <td className="clickable" title="Click: technical description" onClick={() => setDetail({ kind: 'instance', data: r })}>{r.name}</td>
                      <td><span className={`tier-badge tier-${r.tier}`}>{r.tier}</span></td>
                      {data.matrix.contracts.map((c) => {
                        const right = r.cells[c.id];
                        const m = right && ({
                          O: ['O', 'right-o', 'offers this contract'],
                          R: ['R', 'right-r', 'read'],
                          W: ['W', 'right-w', 'write'],
                          RW: ['R·W', 'right-rw', 'read + write'],
                          use: ['·', 'right-use', 'uses this service'],
                        }[right] || [right, 'right-use', right]);
                        return (
                          <td key={c.id} className="cell-center">
                            {m && <span className={`right ${m[1]}`} title={m[2]}>{m[0]}</span>}
                          </td>
                        );
                      })}
                      <td><StatusPill status={r.status} /></td>
                    </tr>
                  ))}
                  {data.matrix.rows.length === 0 && <tr><td colSpan="10" className="empty">Catalogue unavailable.</td></tr>}
                </tbody>
              </table>
            </div>
            <p className="section-intro matrix-legend">
              <span className="right right-o">O</span> offers it ·
              <span className="right right-r">R</span> read ·
              <span className="right right-w">W</span> write ·
              <span className="right right-rw">R·W</span> read+write ·
              <span className="right right-use">·</span> uses the service —
              all access is gateway-enforced &amp; audited. T2 (sensitive) and T0 (sandbox) have no instances yet.
            </p>
          </section>
        )}
      </main>

      {detail?.kind === 'agent' && detail.data.id === 'knowledge-janitor'
        ? (
          <div className="modal-overlay" onClick={() => setDetail(null)}>
            <div className="modal" onClick={(e) => e.stopPropagation()}>
              <button className="modal-close" onClick={() => setDetail(null)} aria-label="Close">×</button>
              <JanitorPanel />
            </div>
          </div>
        )
        : detail?.kind === 'agent'
          ? <AgentDetailModal agent={detail.data} packet={agentPacket} registryEntry={data.registry[detail.data.id]} placements={data.placements} onClose={() => setDetail(null)} />
          : <DetailModal detail={detail} onClose={() => setDetail(null)} />}
    </div>
  );
}

function handleLogoutFactory(logout) {
  return () => logout({ logoutParams: { returnTo: window.location.origin } });
}

export default App;
